import axios from 'axios';
import { useMemo } from 'react';
import { apiUrl } from '../../apiBase';
import { createDocEntryStore } from './docStore';
import { haltGate } from './runHalt';
import { readState, writeState } from './docState';
import { estimatePictures } from './dimensionsRun';
import { getSubmodelFilter, useSubmodelFilter } from './submodelFilterStore';
import { getSubmodels, useSubmodels } from './listingStore';
import { groupOfCategory, type PartItem } from './partsRun';
import { useCategories } from './catalogCategories';
import { cropForTrace, shrinkDataUrl } from './partCrop';
import { ASTRA_MODEL } from './models';
import type { Outline } from './realImages';

// WHERE EACH PART ACTUALLY IS, in the photographs of the finished product.
//
// The manual draws a part; the listing photographs the thing it ends up inside. Between
// those two there was nothing — and for the parts you can see from outside, the photograph
// is the better picture by a distance: it is the real object, in real light, in its real
// finish. A crop of it is a better colour source than a generated photo and a better
// generation input than a line drawing.
//
// ONE CALL PER PHOTOGRAPH, ASKED ABOUT EVERY PART. Not one call per part: it is cheaper,
// and telling a wood button from the end of a dowel means seeing both candidates in one
// frame and assigning each of them once.
//
// MOST PARTS ARE NOT THERE, and that is the answer rather than a failure. The furniture is
// assembled: the screws are driven, the dowels are in the joints, the tools are away. Three
// hits out of fourteen parts is a good run, and an empty answer is a legitimate one.
//
// BOXES ARE STORED, NEVER PIXELS. A crop is a photograph's URL and a rectangle, so it costs
// nothing per part, re-cuts at any zoom, and can be corrected by hand later. The same shape
// the parts table already uses for the drawings it cuts out of the PDF.

export type ListingCrop = {
  // The photograph it was found in, and the rectangle within it (0..1 from the top left).
  url: string;
  bbox: [number, number, number, number];
  // THE SAME RECTANGLE BEFORE THE PADDING, as the model sent it. `bbox` has a margin added to
  // it server-side for drawing and for sampling, and that margin cannot be taken back off by
  // arithmetic — it clamps at the picture's edges, and the numbers have changed once already.
  //
  // The trace pass needs the unpadded one for both of its pictures: it cuts its own frame
  // around it (a margin for reading an edge is not a margin for showing a box) and it draws
  // it on the second copy, as the hint saying which thing in the frame is the part. Absent on
  // crops stored before this was kept, which trace exactly as they always did.
  rawBbox?: [number, number, number, number];
  // HOW FAR FROM THE CAMERA this part is in THIS photograph, in one word — the finding call's
  // own answer, since it is the only call that sees the whole object and can judge one part
  // against the rest of it.
  //
  // It is what a scale read off this box is worth: a part behind the others spans fewer
  // pixels for the same millimetres, so its mm-per-pixel comes out high and comparing it
  // against a near part's is comparing two distances. 'spans' is a part that runs front to
  // back — a seat, a side rail — and is neither.
  //
  // Absent on crops found before it was asked for, which is not 'near'.
  depth?: 'near' | 'far' | 'spans';
  // WHETHER THE PART IS REALLY IN VIEW in this box: false where it is mostly hidden behind
  // something or cut off by the photograph's edge. The finding call's own answer, and the one
  // thing the selection cannot work out for itself (see listingSelectRun). Absent on crops found
  // before it was asked for, which count as clear.
  clear?: boolean;
  // WHICH PIXELS INSIDE THAT RECTANGLE ARE THE PART: a polygon tracing it, as [x, y] pairs
  // normalized 0..1 against the photograph — the same space as `bbox`.
  //
  // Because almost no part is a rectangle. A chair's rear leg frame is a diagonal through its
  // own box and three quarters of that box is the room behind it, so a colour read from the
  // rectangle is a colour of the wall. The outline is what says which pixels are the part.
  //
  // A POLYGON AND NOT A MASK IMAGE, which is what the model was asked for first. Asked for a
  // base64 PNG it answered three different ways across three calls — a reference to an image
  // part, a 65 KB inline blob, and a polygon — and the polygon is the one it volunteers. It is
  // also better in every way that matters here: three hundred bytes instead of thirty
  // kilobytes, in the photograph's own coordinates so nothing has to be aligned against a
  // padded box, correct at any zoom, and drawable — which it now is, on the tile and in the
  // zoom, by the same BoxOverlay that draws the box.
  //
  // SEVERAL OF THEM, because a part is often several shapes on one photograph: a chair's rear
  // frame is a backrest and two legs with the room showing between them, and a side rail is
  // two shapes when a leg stands in front of its middle. Asked for one polygon the model traced
  // each piece correctly and then ran them together, which turns the join between two pieces
  // into an edge and fills what it spans — the floor, the wall, the seat — with this part.
  outlines?: Outline[];
  // ONE RING, from before there could be several. Only read, never written: a crop stored then
  // still draws and still gives its colour, and a re-run replaces it with the list.
  outline?: Outline;
  // TURNED OFF BY HAND. The model found it; somebody looked and disagreed.
  //
  // Struck through rather than deleted, because the two are different facts and only one of
  // them is reversible. A deleted box is indistinguishable from a box that was never found,
  // so the next run — and the next reader — would have to make the same judgement again;
  // this one stays on the picture, hatched, saying a person has already been here.
  off?: boolean;
  // WHAT THE TRACE CALL SAID ABOUT THIS BOX — the second pass's reasoning and raw reply.
  //
  // On the crop and not beside the photograph's note, because that is what one trace call is
  // about: this rectangle, cut out, one part. A photograph with six boxes on it is six of
  // these and one CropNote, and putting them in the same place would say the six came back
  // together. Same shape as the photograph's note, being the same two things.
  note?: CropNote;
};

// By part INDEX, like every other per-part answer in this phase. A re-extraction renumbers
// the list and invalidates this along with everything else, which is correct — they are
// answers about a list that no longer exists.
export type ByPart = Record<number, ListingCrop[]>;

// WHAT ONE PHOTOGRAPH ANSWERED, kept by its URL: the model's reasoning and its raw reply.
//
// This row's answers are rectangles, and a rectangle is a thing you can only accept or
// dismiss. The sentence behind it — "the near chair's buttons are in shadow" — is a claim
// you can argue with, and it is the only way to read a photograph that came back empty:
// whether the model looked and said no, or never understood what it was looking for.
//
// By URL rather than by part, because that is what one call is about: one photograph, every
// part, one reply.
// `unreadable` is the third thing one can say, and the one an empty answer cannot: the reply
// arrived, and nothing in it could be read. A model that stops mid-rectangle leaves a list
// with a bracket missing — the server salvages every element that finished (see _json_array)
// and says so when none did. Without it, a call that fell over mid-sentence and a call that
// looked carefully and found nothing draw the same empty cell.
export type CropNote = { reasoning: string; raw: string; unreadable?: boolean };

type Snapshot = {
  busy: boolean;
  byPart: ByPart;
  notes: Record<string, CropNote>;
  // How many photographs were looked at, so "nothing found" can be told from "never run".
  looked: number;
  // WHICH ONES, for the run that is out right now — and not persisted, because it is a fact
  // about a call in flight and means nothing once it lands.
  //
  // `looked` is the same answer as a number, and a number cannot say which tile is finished.
  // The row shows every photograph in every cell, so a run has to draw the ones that have
  // answered and leave the rest waiting; with a count alone all eight are the same tile and
  // the only honest thing to show is a spinner over the lot. Which is what it did.
  read: string[];
  // WHICH PARTS HAVE BEEN THROUGH A RUN, by index. The other half of the same distinction,
  // per part rather than per document, and it is what a cell needs to know what to say:
  // this row's honest answer is usually nothing at all, and "nothing found" and "not asked
  // yet" are the same empty cell until something remembers which one it is.
  //
  // A scoped run adds its part; a whole run replaces the list, because a whole run is a new
  // answer about which parts are even eligible.
  asked: number[];
  // THE SECOND PASS, as a fraction of the crops it has left to trace. A total of zero is what
  // says it is not running: it is its own press, so nothing else in this snapshot knows.
  //
  // Counted rather than listed, unlike `read`: nothing on screen waits for one particular
  // crop, because every one of them already has its box drawn. What changes as this runs is
  // that shapes appear inside those boxes.
  traced: number;
  toTrace: number;
  // WHICH CROPS THE TRACING HAS NOT COME BACK FOR, by cropKey. `traced` is the same answer as
  // a number, and a number cannot say which tile is still waiting — the same distinction
  // `read` draws for the photographs, per crop instead of per picture.
  //
  // Not persisted: it is a fact about calls in flight.
  tracing: string[];
  error: string | null;
};

// WHICH MODEL FINDS THE BOXES: GPT-6 Astra. Gemini and it were run side by side on the same
// photographs and Astra's boxes were the better of the two, so it is the only one asked now.
// The masks are a different question (tracing inside a box) and stay on the run model.
export const LISTING_CROPS_MODEL = ASTRA_MODEL;

// UNDER ASTRA'S OWN KEYS, the ones its side-by-side runs were saved under: those are the boxes
// that were judged better, and Gemini's older ones under the plain keys are not read — a notice
// Astra has not been run on shows no crops until it is, rather than Gemini's passed off as its.
const cropsKey = (docId: string) => `notice.listingCropsGpt.${docId}`;
const notesKey = (docId: string) => `notice.listingCropsNotesGpt.${docId}`;
const lookedKey = (docId: string) => `notice.listingCropsLookedGpt.${docId}`;
const askedKey = (docId: string) => `notice.listingCropsAskedGpt.${docId}`;

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    byPart: readState<ByPart>(docId, cropsKey(docId), {}),
    notes: readState<Record<string, CropNote>>(docId, notesKey(docId), {}),
    looked: readState<number>(docId, lookedKey(docId), 0),
    read: [],
    asked: readState<number[]>(docId, askedKey(docId), []),
    traced: 0,
    toTrace: 0,
    tracing: [],
    error: null,
  },
}));

// THE SAME PHOTOGRAPH, SERVED FROM HERE. Only for reading pixels: a canvas holding a
// cross-origin image is tainted and getImageData throws on it, so a crop's colour cannot
// be sampled from the retailer's own URL. Displaying stays direct — an <img> needs no
// permission to draw, and routing every thumbnail through the server would put a megabyte
// of chair through it per part.
//
// `full` asks for it at the size its merchant uploaded rather than the gallery's (see
// notice.full_sized) — the size a written build is sent.
export const listingImageSrc = (url: string, full = false) =>
  apiUrl(`/api/notice/listing-photo?url=${encodeURIComponent(url)}${full ? '&full=1' : ''}`);

// WHICH CROP, as one string: the picture it is in and the rectangle within it. A
// photograph can hold the same part several times, so a URL does not identify one — which
// is what the zoom steps through and what a swatch is remembered by.
export const cropKey = (c: ListingCrop) =>
  `${c.url}#${c.bbox.map((n) => n.toFixed(3)).join(',')}`;

// THE SHAPES OF ONE CROP, whichever way it was stored. One list, read in one place, so that
// everything that draws a crop and everything that reads its pixels agree about what it is —
// and so the older single-ring spelling lives in exactly one line of the app.
export const cropRings = (c: ListingCrop): Outline[] =>
  c.outlines?.length ? c.outlines : c.outline?.length ? [c.outline] : [];

export const useListingCrops = store.useSnapshot;

// THE CROPS OF THE SUBMODEL ON SCREEN, and only those. A run reads the photographs of the
// submodel chosen when it was pressed (see MAX_PHOTOS), and its crops are the document's until
// the next run — so on another colourway they were drawn as that colourway's, a dark chair's
// boxes under a pale chair's photographs. Kept to the pictures of THIS submodel's gallery; and
// its parts count as asked only if the run read one of them, so a colourway nobody has looked
// at says "not asked", not "found nothing".
// `submodel`: another colourway than the one chosen — a page showing every colourway at once.
export function useSubmodelListingCrops(docId: string, submodel?: string): Snapshot {
  const snap = store.useSnapshot(docId);
  const submodels = useSubmodels(docId);
  const chosen = useSubmodelFilter(docId);
  const filter = submodel ?? chosen;
  return useMemo(() => {
    const gallery = new Set(submodels.find((s) => s.id === filter)?.gallery ?? []);
    // No submodels at all: one product, nothing to tell apart.
    if (!submodels.length) return snap;
    const byPart: ByPart = {};
    for (const [at, crops] of Object.entries(snap.byPart)) {
      byPart[Number(at)] = crops.filter((c) => gallery.has(c.url));
    }
    const seen = Object.keys(snap.notes).some((url) => gallery.has(url))
      || Object.values(snap.byPart).some((crops) => crops.some((c) => gallery.has(c.url)));
    return { ...snap, byPart, asked: seen ? snap.asked : [] };
  }, [snap, submodels, filter]);
}
export const getListingCrops = (docId: string): ByPart => store.get(docId).byPart;

// TURN ONE DETECTION OFF, OR BACK ON: the part, the photograph, and which of that
// photograph's boxes — which is how the zoom identifies a box it was handed, since the
// boxes for one picture travel in the order they are stored.
//
// Written through immediately. It is a judgement somebody made by looking, and it costs
// more to make again than it does to keep.
export function toggleListingCrop(docId: string, part: number, url: string, nth: number): void {
  const byPart = store.get(docId).byPart;
  const mine = byPart[part] ?? [];
  let seen = 0;
  const next = mine.map((c) => (c.url === url && seen++ === nth ? { ...c, off: !c.off } : c));
  const all = { ...byPart, [part]: next };
  writeState(docId, cropsKey(docId), all);
  store.patch(docId, { byPart: all });
}

// HOW MANY PHOTOGRAPHS ARE WORTH READING. Eight.
//
// This took every picture of every submodel at first, which on one chair was THIRTY-FOUR —
// a call each, a bill each, and about two minutes. Almost all of them are the same chair
// from angles ten degrees apart, in two colours, and a button either shows in that view or
// it does not: the thirty-fourth photograph is the fourth purchase of an answer already
// given.
//
// That argument is not new here. estimatePictures was written against exactly it — "a
// listing strip is mostly the same chair from angles ten degrees apart, and three of those
// are one picture bought three times" — and it already picks one picture per NAMED VIEW
// before falling back to the rest. So this asks it rather than making a second, worse copy
// of the same reasoning.
//
// Eight rather than its usual three because the questions differ. An estimate needs
// something to hold a ruler against and any clear view will do; this needs the ONE view
// that happens to show a small part, which may be a detail shot nobody would have picked.
// Distinct views are exactly what buys that, and eight of them is the whole strip once the
// near-duplicates are gone.
const MAX_PHOTOS = 8;

// The photographs to look through: THIS SUBMODEL's, distinct views first, dimension
// drawings left out — those are line art with measurements on, and no part is findable in
// one.
//
// One submodel because a crop is a picture of the part in a colour, shown in a row about
// that colour. Reading every submodel also multiplied the bill by however many colours the
// product comes in, to answer the same question about the same geometry.
// ONE SET OF CROPS PER SUBMODEL, kept in one store: a crop already says which photograph it was
// cut from, and a photograph belongs to exactly one submodel's gallery. So a run REPLACES the
// crops of the submodel it reads and leaves every other submodel's standing — finding the parts
// in the pale chair no longer throws away where they were in the dark one. Null with no
// submodels: one product, every crop its own.
const ofSubmodel = (
  docId: string, submodel: string = getSubmodelFilter(docId),
): ((url: string) => boolean) | null => {
  const submodels = getSubmodels(docId);
  if (!submodels.length) return null;
  const gallery = new Set(submodels.find((s) => s.id === submodel)?.gallery ?? []);
  return (url) => gallery.has(url);
};
// What a run on this submodel keeps of one part's crops: the other submodels'.
const othersOf = (docId: string, submodel?: string) => {
  const mine = ofSubmodel(docId, submodel);
  return (crops: ListingCrop[] | undefined) => (mine ? (crops ?? []).filter((c) => !mine(c.url)) : []);
};

export const listingPictures = (docId: string, submodel: string = getSubmodelFilter(docId)): string[] =>
  estimatePictures(docId, MAX_PHOTOS, submodel);

// WHICH PARTS ARE WORTH LOOKING FOR AT ALL.
//
// Not the tools. A photograph of a finished chair does not contain the allen key that
// built it — the tools were put away before the picture was taken, and on the listings
// that do lay one beside the product it is a picture of a tool, which is a thing the
// catalogue already has and nothing about this chair.
//
// Not the hardware either, and for the reason the prompt has always spent its
// length on: the furniture is ASSEMBLED. The screws are driven, the dowels are inside the
// joints, the barrel nuts are inside the panels. Asking about them is asking a model to
// find something the photograph does not show, which is the question it is worst at
// refusing — and every one of those answers is a box on a plausible-looking shadow.
//
// What is left is the parts you can still see — the components, which ARE the object being
// photographed. NOT THE COVERS EITHER, though a wood button is on show on the finished chair: a
// few pixels of a photograph are a crop of the chair around it, and the manual draws the cover
// itself, where it goes. They go to the pages with the rest of the hardware (see wantsPageCrop),
// as the felt pads always did.
export const wantsListingCrop = (p: PartItem, category = ''): boolean => {
  if (p.tool) return false;
  const cat = (category || p.category || '').trim();
  const group = groupOfCategory(cat);
  if (group === 'Tools') return false;
  return group === 'Components';
};

// The parts a run would ask about, as indices into the list — the eligible ones, narrowed
// to the open part when there is one. Shared by the run and by the prompt the badge shows,
// because a preview of a different question to the one the button sends is worse than none.
export const listingCropAsks = (
  parts: PartItem[], categories: string[], only?: number,
): number[] =>
  parts
    .map((_, i) => i)
    .filter((i) => (only == null || i === only) && wantsListingCrop(parts[i], categories[i] ?? ''));

// THE SAME LIST, AS A COMPONENT HAS TO ASK FOR IT.
//
// Eligibility is decided by the ONTOLOGY — which category is hardware, which is a component —
// and `groupOfCategory` reads that from a module cache with no subscription in it. A cache
// that is still empty answers "Components" for everything, which is its documented default and
// the right one for a part nobody has classified; it is the wrong one for a part whose
// classification has not arrived yet.
//
// A plain useMemo over [parts, categories] therefore froze the answer given before the catalog
// loaded, for as long as the panel stayed mounted: every hardware part read as a component, and
// the only parts left to this half were the ones marked `tool`, which are caught by a flag
// rather than by a category. The panel offered two of nine parts and was consistent about it.
//
// So the ontology is a dependency, and it is one HERE rather than in three panels that each
// have to remember it.
export function useListingCropAsks(
  parts: PartItem[], categories: string[], only?: number,
): number[] {
  const ontology = useCategories();
  return useMemo(
    () => listingCropAsks(parts, categories, only),
    [parts, categories, only, ontology],
  );
}

// What one part looks like to this call. Everything that helps FIND it in a photograph and
// nothing that does not: a quantity and a reference say nothing about what to look for.
//
// WHERE IT ENDS UP USED TO TRAVEL WITH IT, one line lifted out of the assembly steps that
// name the part. It is gone from the question, so it is gone from here: a field the prompt
// does not read is a step-text lookup per part per run, billed in the payload and paid for
// in nothing.
// Exported with the two above, for the same reason: the page call describes a part to the same
// server block, and a second spelling of it would be a second question.
//
// THE LETTER travels too, and it is read by the PAGE call alone. A listing photograph has no
// callouts on it, so there the letter is a fact about a document the model is not looking at;
// a step drawing labels everything it shows with one, which makes it the strongest thing that
// can be said about where a part is in that picture. The server prints it only in the branch
// that can use it (see _listing_desc).
export const forPrompt = (part: PartItem, index: number) => ({
  tag: String(index),
  nameEn: part.nameEn || part.name,
  description: part.description,
  qty: part.qty,
  letter: part.letter,
  // Which are tools — the manual search asks what each one is working on (see hardwareCropsRun).
  tool: part.tool,
});

// WHICH PART A REPLY IS ABOUT. Read as the first integer in the tag, not as the whole
// string cast to a number.
//
// The prompt labels each part "Tag 3:" and asks for `"tag": "<tag>"`, and a model reading
// that answers "Tag 3" about as often as it answers "3" — both are honest readings of what
// it was shown. Number("Tag 3") is NaN, which silently dropped EVERY crop of the first
// run: the server logged five parts found in each of a dozen photographs and the table
// stayed empty, because the failure was a guard quietly rejecting good answers.
// Exported for the PAGE call, which reads the same tags out of the same kind of answer — see
// hardwareCropsRun. Two readings of one tag is two opinions about which column a box belongs to.
export const tagIndex = (tag: string, count: number): number | null => {
  const digits = /\d+/.exec(tag || '');
  if (!digits) return null;
  const at = Number(digits[0]);
  return Number.isInteger(at) && at >= 0 && at < count ? at : null;
};

// THE MANUAL'S OWN DRAWINGS, to go with the question — one per part asked about, shrunk.
//
// The description is a paraphrase of a shape and the manual drew the shape; the drawing is
// the one thing in this call that is not somebody's words for a silhouette.
//
// Shrunk ONCE PER RUN rather than per photograph: the same five drawings go to all eight
// calls, and the table's crops are cut from a 2400px page because that picture is zoomed
// elsewhere. A part missing its drawing simply travels without one — the block then says
// nothing about an image, which is exactly what the numbering upstream expects.
export const listingSchemas = async (
  asks: number[], drawings: (string | null)[],
): Promise<{ tag: string; image: string }[]> => {
  const out: { tag: string; image: string }[] = [];
  for (const i of asks) {
    const drawn = drawings[i];
    if (!drawn) continue;
    out.push({ tag: String(i), image: await shrinkDataUrl(drawn) });
  }
  return out;
};

// THE QUESTION THIS BUTTON WOULD SEND, for the badge beside it — built by the server, from
// the very same part blocks the run posts, because a prompt lives in one place and a copy
// of it here would be right until the day somebody edited the other one.
//
// The pictures come from here rather than from the reply: the client is what chooses them
// (see listingPictures), and the badge shows them above the text because they ARE the call.
// One prompt for the whole run — the text is identical across the photographs and only the
// image changes.
//
// SO IT SHOWS ONE PHOTOGRAPH, NOT EIGHT. The run makes a call per picture — `look` posts a
// single `url` — and no call in it has ever received a gallery. Eight of them over this text
// described a call nobody makes, and it broke the text as well as the count: the prompt says
// "IMAGE 1 IS THE PHOTOGRAPH. The images after it are drawings", and each part's block names
// its drawing by number. Listed after eight photographs, "image 2" pointed at another chair.
//
// `photo` says WHICH one, for a badge opened beside a photograph that has already answered.
// Without it the first is shown, as the specimen of eight identical questions — which is what
// the panel's own title says it is.
export async function listingCropsPrompt(
  docId: string, parts: PartItem[], categories: string[] = [],
  drawings: (string | null)[] = [], only?: number, photo?: string,
): Promise<{ prompt: string; urls: string[]; model: string }> {
  const asks = listingCropAsks(parts, categories, only);
  if (!asks.length) return { prompt: '', urls: [], model: LISTING_CROPS_MODEL };
  const { data } = await axios.post<{ prompt?: string }>('/api/notice/listing-crops', {
    preview: true,
    parts: asks.map((i) => forPrompt(parts[i], i)),
    // Which parts WOULD carry a drawing, without carrying one: a hover is not a reason to
    // post five pictures. The preview numbers them from this and reads the same as the call.
    schemas: asks.filter((i) => drawings[i]).map((i) => ({ tag: String(i) })),
  });
  // The photograph, then the drawings that go with it — everything ONE call carries, in the
  // order the prompt numbers them. The drawings are not fetched for this: they are already in
  // the browser, which is where they were cut out of the PDF, so showing them costs nothing
  // even though sending them does. A badge that lists eight pictures over a prompt that talks
  // about image 5 is a preview disagreeing with itself.
  return {
    prompt: data.prompt || '',
    urls: [
      ...(photo ? [photo] : listingPictures(docId).slice(0, 1)),
      ...asks.map((i) => drawings[i]).filter((d): d is string => !!d),
    ],
    // The model the run sends, from the same constant it sends — so the badge's top line
    // cannot say one thing while the button does another.
    model: LISTING_CROPS_MODEL,
  };
}

// THE BOXES AS THE MODEL SENT THEM, read back out of the reply it sent them in.
//
// The stored box is padded (`_padded_bbox`, server side: 5% of its own size, floored at half
// a percent of the frame, capped at three) because a rectangle drawn edge to edge on its
// subject is hard to read and worse to cut from. That padding is not recoverable by
// arithmetic — it clamps at the picture's edges, and the numbers it used have changed once
// already — so the only honest source for the unpadded box is the answer itself, which is
// kept per photograph in its CropNote.
//
// Returned per part index, in the order the answer gave them, which is the order the crops
// were pushed in: the nth box of part P here is the nth crop of part P in the store.
//
// TAGS ARE READ IN EVERY SPELLING THE QUESTION COULD HAVE BEEN READ IN, as the server reads
// them (_listing_tag_lookup): shown "Tag 3:" over "what it is: Seat" and asked for a tag, a
// model answers "Seat" about as often as it answers "3". The stored crops went through that
// lookup on the server; this text did not.
const normTag = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// A BOX AS THE REPLY GAVE IT, and which picture it was in. `image` is 1-based and only ever
// said where the call carried several pictures — a step spanning two pages; absent is the
// first, which is every listing photograph and every single-band step.
export type RawBox = { box: [number, number, number, number]; image: number };

// THE ARRAY IN A REPLY, SALVAGED THE WAY THE SERVER SALVAGES IT. A model that stops in the
// middle of writing leaves a list with a bracket missing, and the boxes it had already
// finished are as good as any it has ever sent — throwing all of them away over the one that
// was cut is the reading that turns a truncated reply into "this part is in no picture".
//
// The same rule as the server's _whole_elements, because it reads the same text: cut at the
// commas seen at depth 1, keep every element that closed, drop the one the text stops inside.
const wholeElements = (text: string, start: number): unknown[] => {
  const pieces: string[] = [];
  let depth = 0;
  let inStr = false;
  let esc = false;
  let at = -1;
  for (let i = start; i < text.length; i += 1) {
    const c = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
    } else if (c === '"') {
      if (depth === 1 && at < 0) at = i;
      inStr = true;
    } else if (c === '[' || c === '{') {
      if (depth >= 1 && at < 0) at = i;
      depth += 1;
    } else if (c === ']' || c === '}') {
      depth -= 1;
      if (depth === 0) {
        if (at >= 0) pieces.push(text.slice(at, i));
        break;
      }
    } else if (depth === 1) {
      if (c === ',') {
        if (at >= 0) { pieces.push(text.slice(at, i)); at = -1; }
      } else if (at < 0 && c.trim()) at = i;
    }
  }
  const out: unknown[] = [];
  for (const piece of pieces) {
    try { out.push(JSON.parse(piece)); } catch { /* the one that was cut */ }
  }
  return out;
};

export const jsonArray = (raw: string): unknown[] | null => {
  const text = raw || '';
  const start = text.indexOf('[');
  if (start < 0) return null;
  const end = text.lastIndexOf(']');
  if (end > start) {
    try {
      const arr: unknown = JSON.parse(text.slice(start, end + 1));
      if (Array.isArray(arr)) return arr;
    } catch { /* salvaged below */ }
  }
  const saved = wholeElements(text, start);
  return saved.length ? saved : null;
};

export function rawListingBoxes(
  raw: string, parts: PartItem[],
): Record<number, RawBox[]> {
  const arr = jsonArray(raw);
  if (!arr) return {};

  const byName = new Map<string, number>();
  parts.forEach((p, i) => {
    for (const said of [String(i), `tag ${i}`, p.nameEn, p.name]) {
      const key = normTag(said || '');
      // The first part to claim a spelling keeps it, as on the server: two parts named the
      // same is a manual naming them the same, and guessing between them is worse.
      if (key && !byName.has(key)) byName.set(key, i);
    }
  });

  const out: Record<number, RawBox[]> = {};
  for (const e of arr as Record<string, unknown>[]) {
    if (!e || typeof e !== 'object') continue;
    const at = byName.get(normTag(String(e.tag ?? '')));
    const box = e.box_2d;
    if (at == null || !Array.isArray(box) || box.length !== 4) continue;
    // [ymin, xmin, ymax, xmax] out of 1000, into this app's [x0, y0, x1, y1] out of 1 —
    // sorted and clamped exactly as the server does it (_box_to_bbox), so the two
    // rectangles the switch flips between differ by the padding and by nothing else. A
    // model that answers with its corners the other way round is answering about the same
    // rectangle, and a negative width would draw as a box escaping the picture.
    const [ymin, xmin, ymax, xmax] = box.map((n) => Number(n) / 1000);
    if ([ymin, xmin, ymax, xmax].some((n) => !Number.isFinite(n))) continue;
    const x0 = Math.max(0, Math.min(xmin, xmax));
    const y0 = Math.max(0, Math.min(ymin, ymax));
    const x1 = Math.min(1, Math.max(xmin, xmax));
    const y1 = Math.min(1, Math.max(ymin, ymax));
    if (x1 <= x0 || y1 <= y0) continue;
    const said = Number(e.image);
    (out[at] ||= []).push({
      box: [x0, y0, x1, y1],
      image: Number.isInteger(said) && said >= 1 ? said : 1,
    });
  }
  return out;
}

// THE SECOND PASS'S QUESTION, for one box. Asked of the route that builds it, like the
// first's — and it takes only the line that differs between them: every trace call in a run
// is the same words with a different part named in the middle of them.
export async function listingTracePrompt(
  name: string, hinted = false,
): Promise<string> {
  const { data } = await axios.post<{ prompt?: string }>('/api/notice/listing-trace', {
    // The paragraph about the second picture is only in the question where the second
    // picture was sent, so `hint` is the same key here as it is on the call — true instead
    // of an image, there being no picture to preview with.
    preview: true, name, ...(hinted ? { hint: true } : {}),
  });
  return data.prompt || '';
}

// How many photographs are read at once. Three, not one and not all of them: the calls are
// independent, so running them in sequence turned a fifteen-second job into two minutes,
// and firing a dozen at a model at once is how a rate limit is discovered. Each one still
// lands on its own and paints as it arrives.
const AT_ONCE = 3;

// ONE PART, OR ALL OF THEM — which is the tab you are on. Pressed under a single part's
// column it answers about that part; pressed under "All parts" it answers about every one,
// exactly as before.
//
// WHAT IT SCOPES IS THE ANSWER, NOT THE QUESTION. The call still describes every part in
// the manual, because both reasons for asking that way survive being interested in one of
// them: the photograph is billed whole whatever is asked about it, so a one-part question
// costs the same as a fourteen-part one; and telling a wood button from the end of a dowel
// means seeing both candidates in the frame and assigning each of them once. Ask about the
// button alone and every domed thing in the picture becomes a candidate for it.
//
// So the narrowing happens on the way back: the crops for other parts are dropped, and the
// answers already stored for them are kept. That is what makes this useful — a part the
// run missed can be asked again on its own without resetting the two it got right, the
// same bargain as the per-part Redo on the drawing row above.
export async function runListingCrops(
  docId: string, parts: PartItem[],
  categories: string[] = [], drawings: (string | null)[] = [], only?: number,
  // WHICH COLOURWAY'S PHOTOGRAPHS — the one on screen unless said. A press over every colourway
  // is this, once per colourway (see ListingCropsInfo).
  submodel: string = getSubmodelFilter(docId),
): Promise<void> {
  const urls = listingPictures(docId, submodel);
  const asks = listingCropAsks(parts, categories, only);
  if (!urls.length || !asks.length) return;
  const halted = haltGate(docId);
  const inRun = new Set(asks);
  const scoped = only ?? null;
  // A whole run starts from nothing, because it is about to answer for everything. A scoped
  // one starts from what is already known and empties ONLY its own part: finding nothing
  // this time is an answer about that part, and it should replace the crop it disagrees
  // with rather than leave it standing.
  //
  // "Nothing" and "empties" are THIS SUBMODEL'S: the other colourways' crops are not what
  // this run reads, and stay (see ofSubmodel).
  const others = othersOf(docId, submodel);
  const was = store.get(docId).byPart;
  const byPart: ByPart = scoped == null
    ? Object.fromEntries(Object.entries(was)
      .map(([at, crops]) => [at, others(crops)] as const)
      .filter(([, crops]) => crops.length))
    : { ...was, [scoped]: others(was[scoped]) };
  // Kept per photograph and overwritten as each one answers again, so a scoped run does not
  // throw away what the other seven said — but the ones THIS run is about to read go now,
  // with the crops they explain.
  const notes: Record<string, CropNote> = { ...store.get(docId).notes };
  for (const url of urls) delete notes[url];

  // NOTHING FROM LAST TIME, said at the start rather than as each photograph lands.
  //
  // Emptying `byPart` and `notes` above empties the LOCAL copies, which reach the store when
  // the first call returns — several seconds later. Until then the row went on drawing the
  // last run's crops under a spinner and a 0/8: past results, on photographs that may not
  // even be the eight being read, looking exactly like an answer.
  //
  // `looked` for the same reason, and it is the older half of this bug: it is persisted — it
  // is what tells "nothing was found" apart from "this never ran" — so a run that only set
  // `busy` left the previous total on screen. On a notice read before this row was capped at
  // eight pictures that read "34/8", a fraction whose halves came from different runs.
  store.patch(docId, {
    busy: true, error: null, looked: 0, read: [], byPart: { ...byPart }, notes: { ...notes },
  });

  // Tagged with the part's index in the WHOLE list, not with its place in this shortened
  // one: the tag is how an answer finds its way back to a column, and the columns did not
  // move because the question got shorter.
  const described = asks.map((i) => forPrompt(parts[i], i));
  const schemas = await listingSchemas(asks, drawings);
  let looked = 0;
  let failed = 0;
  // The photographs this run has finished with, in the order they answered — which is not
  // the order they were sent, since several are in the air at once. A failure counts as
  // finished: the tile stops waiting and shows what it shows, which is the same picture with
  // nothing on it, and the reason is in the note behind it.
  const read: string[] = [];

  const look = async (url: string) => {
    try {
      const { data } = await axios.post<{
        // RECTANGLES ONLY: this call is not asked to trace any more, and the shapes all come
        // from the pass below. See traceCrops.
        crops: {
          tag: string;
          bbox: [number, number, number, number];
          raw_bbox?: [number, number, number, number];
          depth?: 'near' | 'far' | 'spans';
          clear?: boolean;
        }[];
        reasoning?: string;
        raw?: string;
        unreadable?: boolean;
      }>('/api/notice/listing-crops', {
        model: LISTING_CROPS_MODEL, url, parts: described, schemas,
      });
      notes[url] = {
        reasoning: data.reasoning || '',
        raw: data.raw || '',
        ...(data.unreadable ? { unreadable: true } : {}),
      };
      for (const found of data.crops || []) {
        const at = tagIndex(found.tag, parts.length);
        // Only the parts this run asked about. A model answering about a part it was not
        // shown has invented the tag, and a scoped run must not overwrite the answers of
        // parts nobody asked it to touch.
        if (at == null || !inRun.has(at)) {
          // A dropped answer is INVISIBLE otherwise, and this is the line that would have
          // saved a morning: eight photographs returned thirty-three boxes and three of
          // them reached the table, which read on screen as a model finding nothing.
          console.warn('[listing-crops] dropped tag', found.tag, 'asked', asks);
          continue;
        }
        // A rectangle and where it came from. The shape is added by the trace pass below,
        // onto this same object — see ListingCrop.
        (byPart[at] ||= []).push({
          url,
          bbox: found.bbox,
          ...(found.raw_bbox ? { rawBbox: found.raw_bbox } : {}),
          ...(found.depth ? { depth: found.depth } : {}),
          ...(found.clear === false ? { clear: false } : {}),
        });
      }
    } catch (e) {
      // PER PHOTOGRAPH, so one retailer's slow server does not throw away eleven good
      // answers. The run reports failure only if every picture failed — which is why a
      // photograph that fails on its own has to say so somewhere.
      console.warn('[listing-crops] photograph failed', url, e);
      // The failure IS this photograph's answer, and the panel that reads these is where
      // somebody asks why one picture found nothing.
      notes[url] = { reasoning: '', raw: `The call failed: ${e}` };
      failed += 1;
    }
    looked += 1;
    read.push(url);
    store.patch(docId, {
      byPart: { ...byPart }, notes: { ...notes }, looked, read: [...read],
    });
  };

  const queue = [...urls];
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, queue.length) }, async () => {
    // STOPPED: no photograph sent after the halt; the ones in the air land and are kept.
    for (let url = queue.shift(); url && !halted(); url = queue.shift()) await look(url);
  }));

  // AND IT STOPS AT THE BOXES. The second pass used to run off the back of this one, which
  // made one press two questions and one wait the length of both — and the tracing is the
  // half you re-run: a box you can see is right does not want finding again to have its
  // shape drawn a second time. It is its own button now. See runListingMasks.

  // ASKED, only if something was actually read. A run whose every photograph failed has
  // looked at nothing, and marking its parts as answered would turn a network outage into
  // "the part is not in any of these pictures" — the one reading this row cannot argue
  // with, since an empty cell is exactly what a true absence looks like.
  // A STOPPED RUN has not looked at every photograph, so its parts are not answered either: a
  // part absent from the half it read may be in the half it did not.
  const done = !halted() && failed < urls.length;
  const asked = done
    ? [...new Set(scoped == null ? asks : [...store.get(docId).asked, ...asks])]
      .sort((a, b) => a - b)
    : store.get(docId).asked;

  writeState(docId, cropsKey(docId), byPart);
  writeState(docId, notesKey(docId), notes);
  writeState(docId, lookedKey(docId), looked);
  writeState(docId, askedKey(docId), asked);
  // `read` is emptied with `busy`: nothing waits any more, and a list of what the last run
  // got through would be read by the next render as a run still in progress.
  store.patch(docId, {
    busy: false, read: [], asked, traced: 0, toTrace: 0, error: done ? null : 'failed',
  });
}

// How many crops are traced at once. More than the photographs, because these calls are
// smaller in every direction: one part, one crop of a few hundred pixels, one short question
// — where a photograph carries the whole chair, five part descriptions and their drawings.
const TRACE_AT_ONCE = 5;

// THE SECOND PASS: each box, cut out of its photograph and traced on its own.
//
// The first call has to see the whole chair — telling a wood button from the end of a dowel
// means having both in the frame — and in that same frame a side rail is eight percent of the
// width. So it locates well and traces badly: the boxes came back right and the outlines came
// back as wedges of the right part. This asks the second question in the frame that suits it,
// where the part fills the picture and nothing else is in it to be confused with.
//
// The crop's own coordinates come back, and they are mapped into the photograph's here —
// which is the reason the route does not do it: the crop was cut in this browser and only
// this side knows where it came from.
//
// AND IT IS THE ONLY THING THAT DRAWS A SHAPE. The first call was asked for outlines too,
// and its wedges stood wherever this pass said nothing — which put the answer this pass
// exists to replace on the picture, in the one case it was supposed to cover. It is asked
// for rectangles now, so a crop this pass cannot trace simply has no shape: the colour then
// comes off the whole rectangle, wall included, which is visibly wrong rather than plausibly
// wrong.
//
// A SHAPE ALREADY TRACED SURVIVES A LATER EMPTY ANSWER, though. A re-run whose trace failed
// or could not make the part out has learned nothing that unsays the tracing on screen.
async function traceCrops(
  docId: string, parts: PartItem[], model: string, byPart: ByPart, wanted: Set<number>,
  // Which crops are this run's — the submodel's own (see runListingMasks).
  mine: (url: string) => boolean = () => true,
): Promise<Set<string>> {
  // The parts this press is about: the one whose tab is open, or every part with a box.
  const jobs: { at: number; nth: number; crop: ListingCrop }[] = [];
  for (const [key, crops] of Object.entries(byPart)) {
    const at = Number(key);
    if (!wanted.has(at)) continue;
    (crops ?? []).forEach((crop, nth) => { if (mine(crop.url)) jobs.push({ at, nth, crop }); });
  }
  // WHICH CROPS WERE ASKED, by cropKey — the caller puts back the ones a Stop never reached.
  const asked = new Set<string>();
  if (!jobs.length) return asked;
  // STOP SENDS NO NEXT CROP (see runHalt): the ones out land and are kept.
  const halted = haltGate(docId);

  let traced = 0;
  // EVERY ONE OF THEM IS WAITING, until its own answer lands. The tiles read this and put a
  // spinner on the pictures whose shapes are still out — which they can only do because the
  // press cleared those shapes first: a mask on screen during the run that redraws it is the
  // last run's, sitting there looking settled.
  let tracing = jobs.map(({ crop }) => cropKey(crop));
  store.patch(docId, { traced: 0, toTrace: jobs.length, tracing });

  const trace = async ({ at, nth, crop }: typeof jobs[number]) => {
    try {
      // ITS OWN FRAME, cut round the model's own rectangle where there is one — see
      // cropForTrace, which is where the margin and the reason for it are written. Falling
      // back to the stored box keeps crops found before the raw one was kept traceable, and
      // they get no hint: a rectangle drawn where the padded box is would be a hint about our
      // own arithmetic.
      const cut = await cropForTrace(listingImageSrc(crop.url), crop.rawBbox ?? crop.bbox);
      if (cut) {
        const part = parts[at];
        const { data } = await axios.post<{
          outlines?: Outline[]; reasoning?: string; raw?: string;
        }>(
          '/api/notice/listing-trace',
          {
            model,
            image: cut.image,
            ...(crop.rawBbox && cut.hint ? { hint: cut.hint } : {}),
            name: part?.nameEn || part?.name || '',
          },
        );
        const [x0, y0, x1, y1] = cut.frame;
        const [w, h] = [x1 - x0, y1 - y0];
        const rings = (data.outlines ?? [])
          .map((ring) => ring.map(([x, y]) => [x0 + x * w, y0 + y * h] as [number, number]))
          .filter((ring) => ring.length > 2);
        // Written into the SAME object the store already holds, because a crop is identified
        // by nothing else: two instances of one part on one photograph differ only by their
        // rectangles, and the list they sit in is their identity.
        //
        // THE NOTE IS KEPT WHETHER OR NOT THE SHAPE IS. An empty trace is an answer — "I
        // cannot make the part out in this crop" is what the prompt asks for when that is
        // true — and it is exactly the case somebody opens the panel to understand.
        (byPart[at] ?? [])[nth] = {
          ...crop,
          note: { reasoning: data.reasoning || '', raw: (data.raw || '').trim() },
          ...(rings.length ? { outlines: rings } : {}),
        };
      }
    } catch (e) {
      console.warn('[listing-trace] failed', crop.url, e);
      // The failure IS this box's answer, in the place its answer goes — as the photograph's
      // own failure is written into its note. See `look`.
      (byPart[at] ?? [])[nth] = { ...crop, note: { reasoning: '', raw: `The call failed: ${e}` } };
    }
    traced += 1;
    tracing = tracing.filter((k) => k !== cropKey(crop));
    store.patch(docId, { byPart: { ...byPart }, traced, tracing });
  };

  const queue = [...jobs];
  await Promise.all(Array.from({ length: Math.min(TRACE_AT_ONCE, queue.length) }, async () => {
    for (let job = queue.shift(); job && !halted(); job = queue.shift()) {
      asked.add(cropKey(job.crop));
      await trace(job);
    }
  }));
  return asked;
}

// DRAW THE MASKS, on the boxes that are already there. The second half of this row's work,
// and its own press — which is what it wants to be: the shapes are what a bad model, a bad
// crop or a bad day spoils, and re-tracing them costs one small call per box where finding
// them again costs a call per photograph and throws away boxes that were right.
//
// NOTHING TO DO WITHOUT BOXES, which is why the button that calls this is dead until the
// find has run: this pass reads the store and traces what is in it.
export async function runListingMasks(
  docId: string, parts: PartItem[], model: string, only?: number,
): Promise<void> {
  // A copy per part, because the tracing writes each crop back in place: mutating the
  // arrays the store is holding would change what is on screen without a render, and the
  // half-written state would be indistinguishable from the finished one.
  //
  // THIS SUBMODEL'S BOXES ONLY, as the find is: the other colourways' are another run's pictures.
  const mine = ofSubmodel(docId) ?? (() => true);
  const byPart: ByPart = {};
  const wanted = new Set<number>();
  for (const [key, crops] of Object.entries(store.get(docId).byPart)) {
    const at = Number(key);
    byPart[at] = [...(crops ?? [])];
    if ((only == null || at === only) && byPart[at].some((c) => mine(c.url))) wanted.add(at);
  }
  if (!wanted.size) return;

  // NOTHING FROM LAST TIME, said before the first call rather than as each answer lands —
  // the same rule the finding run follows, for the same reason. A shape drawn while the run
  // that replaces it is out belongs to the PREVIOUS run and reads as this one's, and there is
  // no telling the two apart by looking.
  //
  // THE BOXES STAY. They are not what this press is about: it traces the rectangles that are
  // already there, so clearing them would empty the row to redraw what is still true.
  //
  // It also means a trace that fails leaves the crop with no shape at all, where before the
  // old one stood. That is the honest reading — the shape on screen would be the answer to a
  // question asked of a different model, on a different day.
  const before: ByPart = Object.fromEntries([...wanted].map((at) => [at, byPart[at]]));
  for (const at of wanted) {
    byPart[at] = byPart[at].map((c) => {
      if (!mine(c.url)) return c;
      const { outlines, outline, ...rest } = c;
      return rest;
    });
  }
  store.patch(docId, { byPart: { ...byPart }, error: null });

  const asked = await traceCrops(docId, parts, model, byPart, wanted, mine);
  // A CROP A STOP NEVER REACHED keeps the shape it had: it was cleared as about to be asked
  // again, and it never was.
  for (const at of wanted) {
    byPart[at] = byPart[at].map((c, nth) => (mine(c.url) && !asked.has(cropKey(c)) ? before[at][nth] ?? c : c));
  }
  writeState(docId, cropsKey(docId), byPart);
  // The fraction is what says this is running — see the row, which reads `toTrace`.
  store.patch(docId, { byPart, traced: 0, toTrace: 0, tracing: [] });
}
